// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for notification management
import {
  useGetAllNotificationsQuery,
  useGetNotificationByIdQuery,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useSendBroadcastNotificationMutation,
} from '../../../redux/slices/notifications/slice';

export const useGetAllNotifications = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllNotifications,
      isLoading: isGetAllNotificationsLoading,
      isError: isGetAllNotificationsError,
      error: getAllNotificationsError,
      isFetching: isGetAllNotificationsFetching,
      refetch: refetchGetAllNotifications,
    } = useGetAllNotificationsQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllNotifications = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAllNotifications !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAllNotifications.data?.data,
          total: getAllNotifications.data?.total,
        });
      }
    }, [getAllNotifications]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllNotificationsLoading,
        isGetAllNotificationsError,
        getAllNotificationsError,
        isGetAllNotificationsFetching,
        refetchGetAllNotifications,
        getAllNotifications: handleGetAllNotifications,
      }),
      [
        data,
        isGetAllNotificationsLoading,
        isGetAllNotificationsError,
        getAllNotificationsError,
        isGetAllNotificationsFetching,
        refetchGetAllNotifications,
        handleGetAllNotifications,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all notifications:', error);
    throw error;
  }
};

export const useGetNotificationById = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [id, setId] = useState('');

    // api call query
    const {
      data: getNotificationById,
      isLoading: isGetNotificationByIdLoading,
      isError: isGetNotificationByIdError,
      error: getNotificationByIdError,
      refetch: refetchGetNotificationById,
    } = useGetNotificationByIdQuery(id, { skip: skipApi });

    // handlers
    const handleGetNotificationById = async id => {
      setSkipApi(false);
      setId(id);
    };

    // effect
    useEffect(() => {
      if (getNotificationById !== undefined) {
        setData(getNotificationById.data);
      }
    }, [getNotificationById]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetNotificationByIdLoading,
        isGetNotificationByIdError,
        getNotificationByIdError,
        refetchGetNotificationById,
        getNotificationById: handleGetNotificationById,
      }),
      [
        data,
        isGetNotificationByIdLoading,
        isGetNotificationByIdError,
        getNotificationByIdError,
        refetchGetNotificationById,
        handleGetNotificationById,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting notification by ID:', error);
    throw error;
  }
};

export const useCreateNotification = () => {
  try {
    // api call mutation
    const [
      createNotification,
      {
        isLoading: isCreateNotificationLoading,
        isError: isCreateNotificationError,
        error: createNotificationError,
      },
    ] = useCreateNotificationMutation();

    // handlers
    const handleCreateNotification = async data => {
      return await createNotification(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isCreateNotificationLoading,
        isCreateNotificationError,
        createNotificationError,
        createNotification: handleCreateNotification,
      }),
      [
        isCreateNotificationLoading,
        isCreateNotificationError,
        createNotificationError,
        handleCreateNotification,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

export const useUpdateNotification = () => {
  try {
    // api call mutation
    const [
      updateNotification,
      {
        isLoading: isUpdateNotificationLoading,
        isError: isUpdateNotificationError,
        error: updateNotificationError,
      },
    ] = useUpdateNotificationMutation();

    // handlers
    const handleUpdateNotification = async ({ id, data }) => {
      return await updateNotification({ id, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateNotificationLoading,
        isUpdateNotificationError,
        updateNotificationError,
        updateNotification: handleUpdateNotification,
      }),
      [
        isUpdateNotificationLoading,
        isUpdateNotificationError,
        updateNotificationError,
        handleUpdateNotification,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

export const useDeleteNotification = () => {
  try {
    // api call mutation
    const [
      deleteNotification,
      {
        isLoading: isDeleteNotificationLoading,
        isError: isDeleteNotificationError,
        error: deleteNotificationError,
      },
    ] = useDeleteNotificationMutation();

    // handlers
    const handleDeleteNotification = async id => {
      return await deleteNotification({ id });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteNotificationLoading,
        isDeleteNotificationError,
        deleteNotificationError,
        deleteNotification: handleDeleteNotification,
      }),
      [
        isDeleteNotificationLoading,
        isDeleteNotificationError,
        deleteNotificationError,
        handleDeleteNotification,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const useMarkAllNotificationsAsRead = () => {
  try {
    // api call mutation
    const [
      markAllNotificationsAsRead,
      {
        isLoading: isMarkAllNotificationsAsReadLoading,
        isError: isMarkAllNotificationsAsReadError,
        error: markAllNotificationsAsReadError,
      },
    ] = useMarkAllNotificationsAsReadMutation();

    // handlers
    const handleMarkAllNotificationsAsRead = async () => {
      return await markAllNotificationsAsRead();
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isMarkAllNotificationsAsReadLoading,
        isMarkAllNotificationsAsReadError,
        markAllNotificationsAsReadError,
        markAllNotificationsAsRead: handleMarkAllNotificationsAsRead,
      }),
      [
        isMarkAllNotificationsAsReadLoading,
        isMarkAllNotificationsAsReadError,
        markAllNotificationsAsReadError,
        handleMarkAllNotificationsAsRead,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

export const useMarkNotificationAsRead = () => {
  try {
    // api call mutation
    const [
      markNotificationAsRead,
      {
        isLoading: isMarkNotificationAsReadLoading,
        isError: isMarkNotificationAsReadError,
        error: markNotificationAsReadError,
      },
    ] = useMarkNotificationAsReadMutation();

    // handlers
    const handleMarkNotificationAsRead = async ({ id }) => {
      return await markNotificationAsRead({ id });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isMarkNotificationAsReadLoading,
        isMarkNotificationAsReadError,
        markNotificationAsReadError,
        markNotificationAsRead: handleMarkNotificationAsRead,
      }),
      [
        isMarkNotificationAsReadLoading,
        isMarkNotificationAsReadError,
        markNotificationAsReadError,
        handleMarkNotificationAsRead,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const useSendBroadcastNotification = () => {
  try {
    // api call mutation
    const [
      sendBroadcastNotification,
      {
        isLoading: isSendBroadcastNotificationLoading,
        isError: isSendBroadcastNotificationError,
        error: sendBroadcastNotificationError,
      },
    ] = useSendBroadcastNotificationMutation();

    // handlers
    const handleSendBroadcastNotification = async data => {
      return await sendBroadcastNotification(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isSendBroadcastNotificationLoading,
        isSendBroadcastNotificationError,
        sendBroadcastNotificationError,
        sendBroadcastNotification: handleSendBroadcastNotification,
      }),
      [
        isSendBroadcastNotificationLoading,
        isSendBroadcastNotificationError,
        sendBroadcastNotificationError,
        handleSendBroadcastNotification,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error sending broadcast notification:', error);
    throw error;
  }
};
